/**
 * 편성 — 스케줄·캠페인·긴급 오버라이드.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Pause, Pencil, Play } from 'lucide-react';
import type {
  Asset,
  Campaign,
  Daypart,
  Group,
  Me,
  Playlist,
  Schedule,
  ScheduleRule,
  SignageOneClient,
} from '../api/client.js';
import {
  Badge,
  Empty,
  ErrorNote,
  formatDaypart,
  formatDays,
  ItemTitle,
  Loading,
  Modal,
  Panel,
} from '../components/ui.js';
import { DayPicker } from '../components/HoursEditor.js';
import { TimeField } from '../components/TimeField.js';
import { DateField, DateTimeField } from '../components/DateField.js';
import { PlaybackPicker, type PickValue } from '../components/PlaybackPicker.js';
import { HistoryButton } from '../components/HistoryModal.js';
import { HelpTip } from '../components/HelpTip.js';
import { SchedulePreview } from '../components/SchedulePreview.js';
import { analyzeOverlaps, ruleTitle, verdictFor } from '../components/scheduleRules.js';
import { timezoneGroups } from '../components/timezones.js';
import { dayEndIso, dayStartIso, localDate } from '../components/localDate.js';
import { useAction, useAsync } from '../hooks.js';
import { can } from '../capabilities.js';
import { BlockedDeleteModal } from '../components/BlockedDeleteModal.js';
import { blockedBy, type BlockedBy } from '../references.js';
import { ColumnHead, FilterMenu, FilterNote, useTableSort } from '../components/TableHead.js';
import { useUrlQuery, useUrlText } from '../components/urlState.js';
import { applyFilters, filterOptions, fixedOrder, sortRows, type Filters, type SortDir, type SortValue } from '../components/tableView.js';
import { useDirty, useGuardedClose } from '../components/closeGuard.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { copyName } from '../components/copyName.js';
import { PageSelectionNote, runEach, SelectAll, SelectionBar } from '../components/SelectionBar.js';
import { Pager, usePaging } from '../components/Pager.js';
import { paginate } from '../components/paging.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { Disclosure } from '../components/Disclosure.js';
import { IconButton } from '../components/IconButton.js';
import { focusFirstInvalid, NumberInput, numberProblem, type NumberRule } from '../components/form/NumberInput.js';

interface Props {
  client: SignageOneClient;
  /** 권한에 따라 만들기·해제 단추를 낼지 정한다. 서버가 최종 관문이고, 여기서는 감출 뿐이다. */
  me: Me;
}

export function SchedulesScreen({ client, me }: Props): ReactNode {
  /** 펼쳐 둔 스케줄. 접는 것이 기본이라 펼친 것만 기억한다. */
  const [openSchedules, setOpenSchedules] = useState<Set<string>>(new Set());
  const toggleSchedule = (id: string): void =>
    setOpenSchedules((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const schedules = useAsync(() => client.listSchedules(), [client]);
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  /*
   * 규칙이 콘텐츠를 직접 가리킬 수 있어 목록이 필요하다.
   *
   * **보관된 것은 빼고 읽는다.** 고를 수 있게 두면 저장할 때 서버가 막는데, 그때는
   * 이미 규칙을 다 짜 놓은 뒤라 어느 줄이 문제인지 찾아야 한다. 애초에 안 보이는
   * 편이 낫다.
   */
  const assets = useAsync(() => client.allAssets({ archived: 'false' }), [client]);
  const [editing, setEditing] = useState<string | null>(null);
  /** 복제 — 이 스케줄의 값을 채운 새 스케줄 창. */
  const [copying, setCopying] = useState<Schedule | null>(null);
  const [creating, setCreating] = useState(false);
  const [blocked, setBlocked] = useState<{ id: string; name: string; blocked: BlockedBy } | null>(null);
  const action = useAction();

  const items = schedules.data?.items ?? [];
  /* 이름 검색 — 주소에 산다(`?q=`). 스케줄이 많아지면 접힌 카드라도 이름을 눈으로 찾게 된다. */
  const url = useUrlQuery();
  const [searchText, setSearch] = useUrlText(url, 'q');
  const needle = url.get('q').trim().toLocaleLowerCase('ko');
  const shown = needle ? items.filter((schedule) => schedule.name.toLocaleLowerCase('ko').includes(needle)) : items;
  /* 쪽 나누기 — 이름 검색을 바꾸면 1쪽으로(`Pager`). 펼쳐 둔 카드는 쪽을 넘겨도 기억한다. */
  const paging = usePaging(url, 'schedules', needle);
  const pageSlice = paginate(shown, paging.page, paging.size);

  return (
    <div className="screen">
      {schedules.error ? <ErrorNote message={schedules.error} /> : null}
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {blocked ? (
        <BlockedDeleteModal
          subject="schedule"
          name={blocked.name}
          blocked={blocked.blocked}
          force={{
            candidates: items.filter((schedule) => schedule.id !== blocked.id).map(({ id, name }) => ({ id, name })),
            run: async (release) => {
              await client.deleteSchedule(blocked.id, release);
              schedules.reload();
            },
          }}
          onClose={() => setBlocked(null)}
        />
      ) : null}

      <Panel
        title={`스케줄 ${items.length}개`}
        className="screen-fill"
        help="요일·시간대별로 재생할 플레이리스트를 정합니다. 화면이나 그룹에 배정해 사용합니다."
        actions={
          <>
            {items.length > 0 ? (
              <input
                className="input input-search w-full sm:w-auto"
                type="search"
                placeholder="이름 검색"
                aria-label="스케줄 이름 검색"
                value={searchText}
                onChange={(event) => setSearch(event.target.value)}
              />
            ) : null}
            {/* 만들 수 없는 사람에게 [새 스케줄] 을 내밀지 않는다 — 눌러야 알게 되는 권한은 안내가 아니다. */}
            {can(me, 'schedule:write') ? (
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                새 스케줄
              </button>
            ) : null}
          </>
        }
      >
        {needle && items.length > 0 ? (
          <FilterNote shown={shown.length} total={items.length} unit="개" onClear={() => url.set({ q: null })} />
        ) : null}
        {needle && shown.length === 0 && items.length > 0 ? (
          <p className="hint p-4">이름에 “{url.get('q').trim()}” 가 들어간 스케줄이 없습니다.</p>
        ) : null}
        {schedules.loading && items.length === 0 ? <Loading /> : null}

        {!schedules.loading && items.length === 0 ? (
          <Empty
            title="스케줄이 없습니다"
            hint="시간대별로 다른 편성을 재생하려면 새 스케줄을 만드세요."
          />
        ) : null}

        {/*
          스케줄 한 장은 접어 둔다.

          목록에서 먼저 묻는 것은 "어떤 스케줄이 있고 어디에 쓰이나" 다. 규칙은 그 다음
          질문이고, 규칙이 열댓 개인 스케줄 서넛이면 목록이 한 화면을 넘겨 **이름을 훑는
          일조차 스크롤 수색**이 된다. 접힌 채로도 규칙이 몇 개인지는 머리에 적는다.
        */}
        {pageSlice.rows.map((schedule) => {
          const open = openSchedules.has(schedule.id);
          return (
          <div key={schedule.id} className="card">
            <div className="card-head">
              {/*
                머리는 셋으로 나뉜다 — 화살표(접기), **이름(누르면 편집** — 다른 목록과 같다, 2026-10-22), 숫자(접기). 예전에는
                머리 왼쪽 전체가 접기 단추라 이름을 눌러도 규칙이 펼쳐질 뿐 편집이 열리지 않았다. 고칠 수 없는 사람에게는 이름도
                접기다(편집기는 [저장] 을 품고 있다).
              */}
              <div className="card-head-split">
                <button
                  type="button"
                  className="card-toggle"
                  aria-expanded={open}
                  aria-label={`${schedule.name} 규칙 ${open ? '접기' : '펼치기'}`}
                  onClick={() => toggleSchedule(schedule.id)}
                >
                  <ChevronDown
                    className={`card-toggle-chevron ${open ? '' : 'is-closed'}`}
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </button>
                <ItemTitle
                  className=""
                  onOpen={can(me, 'schedule:write') ? () => setEditing(schedule.id) : () => toggleSchedule(schedule.id)}
                  label={can(me, 'schedule:write') ? `${schedule.name} 편집` : undefined}
                >
                  <strong>{schedule.name}</strong>
                </ItemTitle>
                <button
                  type="button"
                  className="card-toggle"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={() => toggleSchedule(schedule.id)}
                >
                  <span>
                    <span className="cell-muted">· {schedule.timezone}</span>
                    {/*
                      고치기 전에 영향 범위를 가늠할 수 있어야 한다. 삭제가 같은 줄의 [⋯] 에
                      있으므로 "지워도 되나" 의 답이 곧 이 숫자다.
                    */}
                    <span className="cell-muted"> · {usageLabel(schedule)}</span>
                    {/* 접힌 채로도 규칙이 몇 개인지는 보여야 한다 — 펼칠지 정하는 값이다. */}
                    <span className="cell-muted">
                      {' '}
                      · {schedule.rules.length > 0 ? `규칙 ${schedule.rules.length}개` : '규칙 없음'}
                    </span>
                  </span>
                </button>
              </div>
              <div className="col-actions">
                {/* 고칠 수 없는 사람에게는 행 동작을 내지 않는다 — 누르면 403 인 링크다. */}
                {can(me, 'schedule:write') ? (
                <>
                <IconButton icon={Pencil} label="편집" subject={schedule.name} onClick={() => setEditing(schedule.id)} />
                {/* 복제·삭제는 [⋯] 뒤로 — 줄마다 빨간 삭제가 손 닿는 자리에 있지 않게(점진적 공개). */}
                <MoreMenu
                  label={`${schedule.name} 작업`}
                  items={[
                    // 원본 값을 채운 새 스케줄 창 — 저장해야 생긴다.
                    { label: '복제', onSelect: () => setCopying(schedule) },
                    {
                      label: '삭제',
                      danger: true,
                      disabled: action.pending,
                      onSelect: async () => {
                        const ok = await confirmDialog({
                          title: '스케줄을 삭제할까요?',
                          body: <><strong>{schedule.name}</strong> — 되돌릴 수 없습니다.</>,
                          confirmLabel: '삭제',
                          danger: true,
                        });
                        if (!ok) return;
                        void action.run(
                          async () => {
                            try {
                              await client.deleteSchedule(schedule.id);
                              return true;
                            } catch (cause) {
                              // 쓰는 곳 때문에 막혔으면 어느 화면·그룹인지 목록으로 보여 준다.
                              const found = blockedBy(cause);
                              if (!found) throw cause;
                              setBlocked({ id: schedule.id, name: schedule.name, blocked: found });
                              return false;
                            }
                          },
                          schedules.reload,
                          (deleted) => (deleted ? `"${schedule.name}" 스케줄을 삭제했습니다` : null),
                        );
                      },
                    },
                  ]}
                />
                </>
                ) : null}
              </div>
            </div>

            {!open ? null : (
              <ul className="rules">
                {/*
                  평소 편성이 맨 윗줄이다.

                  규칙만 늘어놓으면 "그 밖의 시간에는 무엇이 나오나" 를 알려고 편집기를 열어야
                  한다. 규칙이 없는 스케줄이면 화면에 나오는 것이 **오직 이것**인데도 그랬다.
                  편집기와 같은 순서(평소 편성 → 규칙)로, 규칙 줄과 같은 꼴로 적는다.
                */}
                <li>
                  <strong className="rule-name">평소 편성</strong>
                  <Badge>{schedule.rules.length > 0 ? '규칙 외 모든 시간' : '하루 종일'}</Badge>
                  {schedule.default_playlist_id || schedule.default_asset_id ? (
                    <span>
                      {targetName(playlists.data?.items ?? [], assets.data?.items ?? [], {
                        playlist_id: schedule.default_playlist_id,
                        asset_id: schedule.default_asset_id ?? null,
                      })}
                    </span>
                  ) : (
                    <span className="cell-muted">없음(대기 화면)</span>
                  )}
                </li>
                {schedule.rules
                  .slice()
                  .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id))
                  .map((rule) => (
                    <li key={rule.id}>
                      {rule.label.trim() ? <strong className="rule-name">{rule.label}</strong> : null}
                      <Badge tone="info">{formatDays(rule.days)}</Badge>
                      <span className="mono">{formatDaypart(rule.start, rule.end)}</span>
                      <span>
                        {targetName(playlists.data?.items ?? [], assets.data?.items ?? [], rule)}
                      </span>
                      <span className="cell-muted">우선순위 {rule.priority}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
          );
        })}

        <Pager slice={pageSlice} paging={paging} unit="개" />
      </Panel>

      {(creating || editing || copying) && playlists.data && assets.data ? (
        <ScheduleEditor
          client={client}
          scheduleId={editing}
          playlists={playlists.data.items}
          assets={assets.data.items}
          existing={
            copying
              ? { ...copying, name: copyName(copying.name, items.map((other) => other.name)) }
              : (items.find((s) => s.id === editing) ?? null)
          }
          heading={copying ? '스케줄 복제' : undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setCopying(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            setCopying(null);
            schedules.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** 사용처를 한 줄로. 그룹으로만 걸린 것과 아무 데도 안 걸린 것은 다른 상태다. */
function usageLabel(schedule: Schedule): string {
  if (schedule.display_count === 0 && schedule.group_count === 0) return '미배정';
  const parts = [`화면 ${schedule.display_count}대`];
  if (schedule.group_count > 0) parts.push(`그룹 ${schedule.group_count}개`);
  return parts.join(' · ');
}

function playlistName(playlists: Playlist[], id: string | null): string {
  if (!id) return '(없음)';
  return playlists.find((p) => p.id === id)?.name ?? id;
}

/**
 * 규칙이 무엇을 트는지 한 줄로.
 *
 * 콘텐츠 하나짜리 규칙은 **콘텐츠임을 밝힌다.** 이름만 적으면 목록에서 플레이리스트와
 * 구분되지 않고, "왜 이 시간에 한 장만 나오지" 를 편성 쪽에서 찾게 된다.
 */
export function targetName(
  playlists: Playlist[],
  assets: Asset[],
  rule: Pick<ScheduleRule, 'playlist_id' | 'asset_id'>,
): string {
  if (rule.asset_id) {
    const asset = assets.find((a) => a.id === rule.asset_id);
    return `콘텐츠 · ${asset?.title ?? rule.asset_id}`;
  }
  return playlistName(playlists, rule.playlist_id);
}

/**
 * 드롭다운 한 칸의 값 ↔ 규칙의 대상.
 *
 * 플레이리스트와 콘텐츠가 한 `<select>` 에 섞이므로 id 만으로는 어느 쪽인지 알 수
 * 없다. 접두사로 종류를 실어 나른다 — 두 목록에 같은 id 가 나올 일은 없지만,
 * 그것에 기대면 언젠가 조용히 틀린다.
 */
export function targetValue(rule: Pick<ScheduleRule, 'playlist_id' | 'asset_id'>): string {
  if (rule.asset_id) return `as:${rule.asset_id}`;
  return rule.playlist_id ? `pl:${rule.playlist_id}` : '';
}

/** 한 칸 값(`pl:…` · `as:…`) ↔ 고르개 값(`PlaybackPicker`). */
export function pickFromTarget(value: string): PickValue | null {
  if (value.startsWith('pl:')) return { kind: 'playlist', id: value.slice(3) };
  if (value.startsWith('as:')) return { kind: 'asset', id: value.slice(3) };
  return null;
}

export function targetFromPick(value: PickValue | null): string {
  if (!value) return '';
  return value.kind === 'asset' ? `as:${value.id}` : `pl:${value.id}`;
}

export function targetPatch(value: string): Pick<ScheduleRule, 'playlist_id' | 'asset_id'> {
  if (value.startsWith('as:')) return { playlist_id: null, asset_id: value.slice(3) };
  if (value.startsWith('pl:')) return { playlist_id: value.slice(3), asset_id: null };
  return { playlist_id: null, asset_id: null };
}

/**
 * 스케줄 편집기.
 *
 * 심야 구간(예: 22:00~02:00)을 **정상 입력으로** 받는다. 이 표기가 처음에는 오타처럼
 * 보이기 때문에, 저장 전에 "(익일)"을 붙여 의도가 맞는지 확인시킨다.
 */
/** 우선순위 칸 — 서버(`scheduleRuleSchema`·캠페인)와 같은 범위. */
const PRIORITY_RULE: NumberRule = { label: '우선순위', min: 0, max: 10_000 };
/** 캠페인 삽입 간격 — 기존 콘텐츠 몇 개마다(서버 `campaignSchema.blend_every`). */
const BLEND_RULE: NumberRule = { label: '삽입 간격', min: 1, max: 100, unit: '개' };

function ScheduleEditor({
  client,
  scheduleId,
  playlists,
  assets,
  existing,
  heading,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  /** 창 제목을 바꿀 때(복제). 빼면 편집·새로 만들기에 맞춘다. */
  heading?: string;
  scheduleId: string | null;
  playlists: Playlist[];
  assets: Asset[];
  existing: {
    name: string;
    timezone: string;
    default_playlist_id: string | null;
    default_asset_id?: string | null;
    rules: ScheduleRule[];
  } | null;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [name, setName] = useState(existing?.name ?? '');
  const [timezone, setTimezone] = useState(existing?.timezone ?? 'Asia/Seoul');
  /*
   * 평소 편성 — 규칙과 같은 한 칸 값(`pl:…` · `as:…` · '')(`targetValue`). 플레이리스트나 콘텐츠 하나(2026-10-21).
   */
  const [defaultTarget, setDefaultTarget] = useState(
    targetValue({ playlist_id: existing?.default_playlist_id ?? null, asset_id: existing?.default_asset_id ?? null }),
  );
  const defaults = targetPatch(defaultTarget);
  const [rules, setRules] = useState<ScheduleRule[]>(existing?.rules ?? []);
  /* 고친 것이 있으면 닫기 전에 묻는다. */
  const close = useGuardedClose(useDirty({ name, timezone, defaultTarget, rules }), onClose);
  /*
   * 들여다보는 규칙 — 둘로 나눈다.
   *
   * 마우스를 올린 규칙(`hoverRule`)은 오른쪽 그림에서 그 구간과 요일을 밝히기만 한다. 고른
   * 규칙(`activeRule`, 누르거나 칸에 들어간 것)은 거기에 더해 그림의 요일을 그 규칙이 있는
   * 날로 옮긴다. 목록을 훑으며 지나가는 손에 요일까지 따라 바뀌면 그림이 계속 들썩인다.
   */
  const [hoverRule, setHoverRule] = useState<string | null>(null);
  const [activeRule, setActiveRule] = useState<string | null>(null);
  /**
   * 우선순위 칸을 비운 규칙. 비운 동안 규칙의 값은 그대로 두고(겹침 계산이 숫자를 쓴다) 칸만 비워 보이며,
   * 저장할 때 막는다. 겹침이 풀려 칸이 사라지면 원래 값으로 저장된다.
   */
  const [blankPriority, setBlankPriority] = useState<ReadonlySet<string>>(new Set());
  /** [저장] 을 눌렀는데 우선순위를 고쳐야 할 규칙이 있었다 — 그 칸을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const action = useAction();

  /**
   * 그 규칙으로 데려간다 — 그림의 구간을 눌렀을 때.
   *
   * 규칙이 열 개쯤 되면 "이 구간이 어느 규칙인지" 를 카드에서 찾는 일이 곧 스크롤 수색이
   * 된다. 자리를 찾아 주고 잠깐 도드라지게 해서, 눌렀을 때 무엇이 일어났는지 보이게 한다.
   */
  const focusOnRule = (ruleId: string): void => {
    setActiveRule(ruleId);
    const card = document.querySelector<HTMLElement>(`[data-rule="${CSS.escape(ruleId)}"]`);
    card?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    card?.querySelector<HTMLInputElement>('input')?.focus();
  };

  /*
   * 타임존 목록은 타임존을 바꿀 때만 다시 만든다. 418개를 담고 오프셋까지 계산하는
   * 일이라, 규칙을 한 글자 고칠 때마다 다시 만들면 입력이 눈에 띄게 끊긴다.
   *
   * `timezone` 을 의존성에 넣는 이유는 "현재 설정" 묶음 때문이다 — 목록에 없는 값이
   * 들어와 있으면 그 값을 맨 앞에 따로 넣어야 하고, 그 판단이 현재 값에 달려 있다.
   */
  const timezoneOptions = useMemo(() => timezoneGroups(timezone), [timezone]);

  // 규칙이 바뀔 때마다 다시 센다. 규칙 수는 최대 200이고 한 주를 5분 간격으로 훑을 뿐이라 싸다.
  const overlaps = useMemo(() => analyzeOverlaps(rules), [rules]);
  const hiddenRules = rules.filter((rule) => verdictFor(overlaps.get(rule.id)) === 'hidden');

  /*
   * 시각을 비운 규칙은 저장할 수 없다.
   *
   * `<input type="time">` 은 못 읽는 값을 빈 문자열로 만든다 — 지우거나 24:00 을 넣으면
   * 그렇게 된다. 그대로 보내면 서버가 400 으로 막지만 어느 규칙의 무엇이 문제인지는
   * 말해 주지 않는다. 여기서 먼저 잡아 이름을 대며 알려 준다.
   */
  const incompleteRules = rules.filter((rule) => !HHMM.test(rule.start) || !HHMM.test(rule.end));

  /*
   * 요일을 모두 끈 규칙도 저장할 수 없다 — 서버가 "요일을 최소 하나 선택해야 합니다" 로 막는데,
   * 어느 규칙인지는 말해 주지 않는다. 고르던 손으로 마지막 요일을 끄는 일은 흔하다.
   */
  const daylessRules = rules.filter((rule) => rule.days.length === 0);

  /** 지금 보이는 우선순위 칸(겹치는 규칙에만 있다)의 문제 — 비웠거나 범위 밖. */
  const priorityProblem = (rule: ScheduleRule): string | null =>
    verdictFor(overlaps.get(rule.id)) === 'ok'
      ? null
      : numberProblem(blankPriority.has(rule.id) ? null : rule.priority, PRIORITY_RULE);
  const priorityIssues = rules.filter((rule) => priorityProblem(rule) !== null);

  /*
   * **직전 상태에서 더한다.**
   *
   * `setRules([...rules, ...])` 는 렌더 시점의 `rules` 를 붙든다. 같은 틱에 두 번
   * 눌리면 둘 다 같은 배열을 보고 더해서 **하나만 남는다.** 규칙 id 도 `Date.now()`
   * 라 같은 밀리초에 두 번 만들면 겹치고, 서버가 "규칙 id 가 중복되었습니다" 로 막는다.
   */
  const addRule = (): void => {
    setRules((prev) => [
      ...prev,
      {
        id: `rule-${Date.now().toString(36)}-${prev.length}`,
        label: '',
        days: [1, 2, 3, 4, 5],
        start: '09:00',
        end: '18:00',
        /*
         * 기본은 플레이리스트다. 하나도 없으면 콘텐츠 첫 줄로 시작한다 —
         * 둘 다 없으면 규칙을 추가하는 버튼 자체가 잠겨 있다.
         */
        playlist_id: playlists[0]?.id ?? null,
        asset_id: playlists[0] ? null : (assets[0]?.id ?? null),
        priority: 0,
      },
    ]);
    /*
     * 더한 규칙은 목록 맨 뒤에 붙는다. 규칙이 몇 개만 돼도 화면 밖이라, 누른 사람은
     * "추가가 안 됐나" 로 읽는다 — 다음 렌더에 그 카드로 데려가 이름부터 받는다.
     */
    setActiveRule(null);
    window.requestAnimationFrame(() => {
      const cards = document.querySelectorAll<HTMLElement>('[data-rule]');
      const last = cards[cards.length - 1];
      last?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      last?.querySelector<HTMLInputElement>('input')?.focus();
    });
  };

  const updateRule = (index: number, patch: Partial<ScheduleRule>): void => {
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const setPriority = (index: number, ruleId: string, value: number | null): void => {
    setBlankPriority((prev) => {
      const next = new Set(prev);
      if (value === null) next.add(ruleId);
      else next.delete(ruleId);
      return next;
    });
    if (value !== null) updateRule(index, { priority: value });
  };

  const removeRule = (index: number): void => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const save = (): void => {
    if (priorityIssues.length > 0) {
      setShowErrors(true);
      focusFirstInvalid(formRef.current);
      return;
    }
    setShowErrors(false);
    const payload = {
      name,
      timezone,
      // 둘 다 보낸다 — 한쪽을 고르면 다른 쪽은 비운다(서버도 같은 규칙).
      default_playlist_id: defaults.playlist_id,
      default_asset_id: defaults.asset_id,
      rules,
    };
    void action.run(
      () => (scheduleId ? client.updateSchedule(scheduleId, payload) : client.createSchedule(payload)),
      onSaved,
      scheduleId ? `"${name}" 스케줄을 저장했습니다` : `"${name}" 스케줄을 만들었습니다`,
    );
  };

  return (
    <Modal title={heading ?? (scheduleId ? '스케줄 편집' : '새 스케줄')} onClose={close} wide>
      {/*
        **왼쪽은 정하는 곳, 오른쪽은 그 결과.**

        스케줄은 사람 머릿속에서 한 문장이다 — "평소에는 A를 틀고, 이 시간대에는 B를 튼다".
        그 문장을 왼쪽에 세로로 놓고(이름 → 평소에 트는 것 → 시간대 규칙), 오른쪽에는
        지금까지 정한 것이 하루로 어떻게 펼쳐지는지를 **스크롤을 따라다니며** 보여준다.

        예전에는 한 단으로 쌓여 있어서, 규칙을 셋만 넘겨도 고치는 칸과 그 결과가 같은
        화면에 들어오지 않았다 — 결과를 보려면 위로 올라가야 하고, 올라가면 무엇을
        고치던 중이었는지 잊는다. 심야 구간과 겹침은 머릿속으로 검증할 수 없고, 틀린
        편성은 그 시간이 와야 드러난다 — 대개 아무도 안 보는 새벽에.

        규칙과 그림은 서로를 가리킨다: 규칙에 손을 올리면 그 규칙이 만든 구간이 막대에서
        도드라지고, 구간을 누르면 그 규칙으로 데려간다.
      */}
      <div className="sched-editor">
        <div className="sched-editor__form form" ref={formRef}>
          {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

          <label className="field">
            <span>이름</span>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 매장 운영시간"
              required
            />
          </label>

          {/*
            "기본 플레이리스트" 였다. 그 이름은 시스템의 말이고, 운영자가 정하는 것은
            "평소에 무엇을 트나" 다.

            규칙 칸과 같은 한 칸 고르개 — 플레이리스트 또는 콘텐츠 하나(2026-10-21). 평소엔 영상 한 편만 도는
            곳이 한 항목짜리 플레이리스트를 따로 만들지 않게.
          */}
          <div className="field">
            <span>
              평소 편성
              <HelpTip
                text="시간대 규칙에 해당하지 않는 모든 시간에 재생합니다. 플레이리스트나 콘텐츠 하나를 고릅니다."
                label="평소 편성 도움말"
              />
            </span>
            {/* 지금 값이 목록에 없으면(보관·삭제된 콘텐츠) 고르개가 그렇다고 말한다 — 빈칸이면 "없음" 으로 읽힌다. */}
            <PlaybackPicker
              label="평소 편성"
              none="없음(대기 화면)"
              value={pickFromTarget(defaultTarget)}
              sources={{ playlists, assets }}
              onChange={(next) => setDefaultTarget(targetFromPick(next))}
            />
          </div>

          <div className="field">
            <span>
              시간대 규칙
              <HelpTip
                text="규칙은 요일 단위로 반복됩니다. 연휴 등 특정 날짜만 다르게 하려면 캠페인을 사용하세요."
                label="시간대 규칙 도움말"
              />
            </span>

            {rules.length === 0 ? <p className="hint">규칙이 없어 하루 종일 평소 편성을 재생합니다.</p> : null}

            {rules.map((rule, index) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                rules={rules}
                playlists={playlists}
                assets={assets}
                overlaps={overlaps}
                active={activeRule === rule.id}
                /* 손을 올리면 이 규칙이 하루·한 주의 어디인지 오른쪽에서 밝혀지고, 고르면 그 요일로 간다. */
                onHover={(on) => setHoverRule((current) => (on ? rule.id : current === rule.id ? null : current))}
                onActivate={() => setActiveRule(rule.id)}
                onDeactivate={() => setActiveRule((current) => (current === rule.id ? null : current))}
                onChange={(patch) => updateRule(index, patch)}
                priorityBlank={blankPriority.has(rule.id)}
                priorityError={showErrors ? priorityProblem(rule) : null}
                onPriority={(value) => setPriority(index, rule.id, value)}
                onRemove={() => removeRule(index)}
              />
            ))}

            <button type="button" className="btn" onClick={addRule} disabled={playlists.length === 0 && assets.length === 0}>
              규칙 추가
            </button>

            {/*
              규칙은 요일로만 걸린다. 특정 날짜(연휴·행사)는 여기서 표현할 수 없는데,
              그 사실을 말해 주지 않으면 운영자는 그날 손으로 배정을 바꾸고 되돌리는 것을
              잊는다. 되돌리기를 잊은 편성은 아무도 눈치채지 못한다.
              (그 안내는 "시간대 규칙" 옆 물음표에 있다.)
            */}
          </div>
        </div>

        {/*
          결과.

          규칙이 없을 때도 의미가 있다 — "하루 종일 이것이 나갑니다" 가 곧 스케줄의
          출발점이고, 그 그림을 본 사람은 규칙이 무엇을 하는 것인지 설명 없이 안다.
        */}
        <div className="sched-editor__preview">
          <div className="sched-editor__preview-sticky">
            <span className="field-label">
              <span>
                하루 미리보기
                <HelpTip text="구간을 누르면 그 구간을 만든 규칙으로 이동합니다." label="하루 미리보기 도움말" />
              </span>
            </span>
            <SchedulePreview
              rules={rules}
              defaultPlaylistId={defaults.playlist_id}
              defaultAssetId={defaults.asset_id}
              timezone={timezone}
              playlists={playlists}
              assets={assets}
              highlightRuleId={hoverRule ?? activeRule}
              followRuleId={activeRule}
              onRulePick={focusOnRule}
            />
            {/*
              타임존은 시각 옆에 있어야 뜻이 통한다. 한 번 정하고 다시 보지 않는 값이므로
              이름 아래(가장 좋은 자리)가 아니라 그림 밑에 둔다.
            */}
            <div className="tz-line">
              <span className="cell-muted">
                시각 기준
                <HelpTip text="화면에 자체 타임존이 설정되어 있으면 화면의 타임존이 우선합니다." label="시각 기준 도움말" />
              </span>
              <select className="input input-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {timezoneOptions.map((group) => (
                  <optgroup key={group.title} label={group.title}>
                    {group.options.map((option) => (
                      <option key={option.zone} value={option.zone}>
                        {option.label}
                        {option.offset ? ` (${option.offset})` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/*
              가려진 규칙은 저장은 되는데 영원히 재생되지 않는다. 목록에는 멀쩡히 보이므로
              나중에 스스로 알아차릴 방법이 없다 — 저장 전에 말해 주는 것이 유일한 기회다.
              막지는 않는다. 잠시 꺼 두려고 일부러 가려 두는 쓰임이 있다.

              경고는 **그림 옆**에 둔다. 규칙을 고치는 동안에도 보여야 하고, 무엇이 가려졌는지는
              그림에서 확인하게 되기 때문이다.
            */}
            {incompleteRules.length > 0 ? (
              <div className="note note-warn" role="status">
                <span>
                  <strong>시각이 비어 있는 규칙</strong> —{' '}
                  {incompleteRules.map((rule) => ruleTitle(rule)).join(', ')}. 시작·종료 시각을 입력해야 저장할 수 있습니다.
                </span>
              </div>
            ) : null}

            {daylessRules.length > 0 ? (
              <div className="note note-warn" role="status">
                <span>
                  <strong>요일이 없는 규칙</strong> —{' '}
                  {daylessRules.map((rule) => ruleTitle(rule)).join(', ')}. 요일을 하나 이상 선택해야 저장할 수 있습니다.
                </span>
              </div>
            ) : null}

            {hiddenRules.length > 0 ? (
              <div className="note note-warn" role="status">
                <span>
                  <strong>재생되지 않는 규칙 {hiddenRules.length}개</strong> —{' '}
                  {hiddenRules.map((rule) => ruleTitle(rule)).join(', ')}. 다른 규칙에 완전히 가려집니다.
                  우선순위를 높이거나 시간대를 좁히세요.
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-actions sched-editor__actions">
          {scheduleId ? (
            <span className="mr-auto">
              <HistoryButton client={client} type="schedule" id={scheduleId} canRestore onRestored={onSaved} />
            </span>
          ) : null}
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={action.pending || !name || incompleteRules.length > 0 || daylessRules.length > 0}
            onClick={save}
          >
            {action.pending ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * 규칙 한 개 — **한 문장으로 읽히게** 놓는다.
 *
 * 예전에는 이름·요일·시작·종료·플레이리스트·우선순위·제거가 한 줄에 늘어서서, 좁은
 * 창에서는 어디서 줄이 바뀔지 알 수 없었고 무엇이 한 규칙인지도 눈에 안 들어왔다.
 * 여기서는 두 줄로 나눈다 — 위는 이름과 상태, 아래는 "[요일] [시각]에는 [이것]".
 *
 * **우선순위는 겹칠 때만 보여준다.** 0~10000 짜리 숫자가 모든 규칙에 붙어 있으면,
 * 겹칠 일이 없는 규칙에도 사용자가 답할 수 없는 질문을 하나씩 던지는 셈이다.
 * 실제로 겹치는 규칙에서만 나타나고, 그때는 누가 이기는지도 함께 말해 준다.
 */
function RuleCard({
  rule,
  rules,
  playlists,
  assets,
  overlaps,
  active,
  onHover,
  onActivate,
  onDeactivate,
  onChange,
  priorityBlank,
  priorityError,
  onPriority,
  onRemove,
}: {
  rule: ScheduleRule;
  rules: readonly ScheduleRule[];
  playlists: Playlist[];
  assets: Asset[];
  overlaps: ReturnType<typeof analyzeOverlaps>;
  /** 고른 규칙인가 — 테두리로 표시한다. 오른쪽 그림이 지금 이 규칙의 요일을 보이고 있다. */
  active: boolean;
  /** 손을 올렸다(true)·뗐다(false) — 오른쪽 그림이 그 구간과 요일을 밝힌다. */
  onHover: (on: boolean) => void;
  /** 이 규칙을 골랐다(누르거나 칸에 들어갔다) — 오른쪽 그림이 그 규칙이 있는 요일로 간다. */
  onActivate: () => void;
  onDeactivate: () => void;
  onChange: (patch: Partial<ScheduleRule>) => void;
  /** 우선순위 칸을 비워 두었다 — 칸을 빈 채로 그린다(규칙의 값은 그대로). */
  priorityBlank: boolean;
  /** [저장] 이 막힌 까닭 — 있으면 칸을 빨갛게 하고 아래에 적는다. */
  priorityError: string | null;
  onPriority: (value: number | null) => void;
  onRemove: () => void;
}): ReactNode {
  const overlap = overlaps.get(rule.id);
  const verdict = verdictFor(overlap);
  const rivals = (overlap?.shadowedBy ?? []).map((id) => ruleTitle(rules.find((r) => r.id === id)));
  const allDay = rule.start === '00:00' && rule.end === '24:00';
  const [naming, setNaming] = useState(false);

  return (
    <div
      className={`rule-card ${active ? 'is-active' : ''}`}
      data-rule={rule.id}
      /* 입력칸이 아닌 곳(카드 바탕·요일 단추)을 눌러도 고른 것으로 친다. */
      onClick={onActivate}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onFocusCapture={onActivate}
      onBlurCapture={onDeactivate}
    >
      <div className="rule-card-head">
        {/*
          이름은 대개 끝까지 비어 있다 — 빈 이름 칸을 규칙마다 맨 앞에 두면 "여기부터 채워야 하나" 로 읽힌다.
          이름이 있거나 [이름 붙이기] 를 눌렀을 때만 칸을 낸다(점진적 공개).
        */}
        {naming || rule.label ? (
          <input
            className="input"
            value={rule.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="이름 (예: 점심 프로모션)"
            aria-label="규칙 이름"
            maxLength={120}
            autoFocus={naming && !rule.label}
          />
        ) : null}
        {/*
          이름이 비어 있어도 어느 규칙인지 알아야 한다 — 언제 무엇을 트는지를 머리에 늘 적어 둔다.
        */}
        <span className="rule-card-when-summary cell-muted">{ruleTitle(rule)}</span>
        {rule.days.length === 0 ? <Badge tone="warn">요일 없음</Badge> : null}
        {verdict === 'hidden' ? <Badge tone="danger">재생 안 됨</Badge> : null}
        {naming || rule.label ? null : (
          <button type="button" className="link" onClick={() => setNaming(true)}>
            이름 붙이기
          </button>
        )}
        <button type="button" className="link link-danger" onClick={onRemove}>
          제거
        </button>
      </div>

      <div className="rule-card-when">
        <DayPicker days={rule.days} onChange={(days) => onChange({ days })} />
        <DayPresets days={rule.days} onChange={(days) => onChange({ days })} />
      </div>

      <div className="rule-card-what">
        {/*
          **종일은 시각 입력으로 표현할 수 없다.**

          종일은 `00:00 ~ 24:00` 인데(시작과 종료가 같으면 "0분"인지 "24시간"인지 알 수
          없어 서버가 거부한다), `<input type="time">` 의 최댓값은 23:59 라 24:00 을
          넣으면 **빈 값이 된다.** 그래서 콘솔만으로는 종일 규칙을 만들 수 없었다.
          시각 입력을 고칠 수는 없으니 버튼으로 표현한다.
        */}
        <button
          type="button"
          className={`link day-preset-btn ${allDay ? 'is-on' : ''}`}
          aria-pressed={allDay}
          onClick={() => onChange(allDay ? { start: '09:00', end: '18:00' } : { start: '00:00', end: '24:00' })}
        >
          종일
        </button>

        {allDay ? null : (
          <>
            <input
              className="input input-time"
              type="time"
              value={rule.start}
              onChange={(e) => onChange({ start: e.target.value })}
              aria-label="시작"
            />
            <span className="cell-muted">~</span>
            <input
              className="input input-time"
              type="time"
              value={rule.end}
              onChange={(e) => onChange({ end: e.target.value })}
              aria-label="종료"
            />
            {rule.end && rule.start && rule.end <= rule.start ? <Badge tone="warn">익일까지</Badge> : null}
          </>
        )}
        <span className="cell-muted">에는</span>
        {/*
          플레이리스트와 콘텐츠를 **한 칸에** 담는다.

          칸을 둘로 나누면(종류 고르기 → 대상 고르기) 클릭이 한 번 늘고, 종류만
          바꿔 놓고 대상을 안 고른 중간 상태가 생긴다. 고르개의 종류 칸·아이콘이
          어느 쪽인지 말해 주므로 한 칸으로 충분하다(`PlaybackPicker`).
        */}
        <PlaybackPicker
          label="재생할 것"
          placeholder="재생할 것 고르기"
          value={pickFromTarget(targetValue(rule))}
          sources={{ playlists, assets }}
          onChange={(next) => onChange(targetPatch(targetFromPick(next)))}
        />
      </div>

      {verdict !== 'ok' ? (
        <div className="rule-card-clash">
          <span className="cell-muted">
            {verdict === 'hidden'
              ? `${rivals.join(', ')}에 완전히 가려져 재생되지 않습니다.`
              : `${rivals.join(', ')}${wagwa(rivals.join(', '))} 겹칩니다.`}
          </span>
          <label className="rule-card-priority">
            <span>
              우선순위
              <HelpTip text="겹치는 시간에는 우선순위가 높은 규칙이 재생됩니다." label="규칙 우선순위 도움말" />
            </span>
            <NumberInput
              rule={PRIORITY_RULE}
              value={priorityBlank ? null : rule.priority}
              invalid={priorityError !== null}
              onChange={onPriority}
              aria-label="우선순위"
            />
          </label>
          {priorityError ? (
            <p className="field-error" role="alert">
              {priorityError}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 요일 빠른 선택.
 *
 * 실제로 쓰이는 조합은 대개 셋 중 하나다. 일곱 개를 하나씩 누르게 두면 흔한 일이
 * 가장 번거로운 일이 된다.
 */
function DayPresets({ days, onChange }: { days: number[]; onChange: (days: number[]) => void }): ReactNode {
  const presets: ReadonlyArray<{ label: string; days: number[] }> = [
    { label: '평일', days: [1, 2, 3, 4, 5] },
    { label: '주말', days: [0, 6] },
    { label: '매일', days: [0, 1, 2, 3, 4, 5, 6] },
  ];
  const same = (a: number[], b: number[]): boolean => a.length === b.length && a.every((d) => b.includes(d));

  return (
    <div className="day-presets">
      {presets.map((preset) => (
        <button
          key={preset.label}
          type="button"
          className={`link ${same(days, preset.days) ? 'is-on' : ''}`}
          onClick={() => onChange(preset.days)}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}

/**
 * 앞말의 받침에 따라 갈리는 조사. 규칙 이름은 사용자가 지은 말이라 미리 정할 수 없다.
 *
 * 한글 음절은 유니코드에서 (초성·중성·종성) 순으로 배열되어 있어, 그 음절 코드에서
 * 28로 나눈 나머지가 0이면 받침이 없다.
 */
export function wagwa(text: string): string {
  const last = text.trim().slice(-1);
  const code = last.charCodeAt(0);
  if (Number.isNaN(code) || code < 0xac00 || code > 0xd7a3) return '와';
  return (code - 0xac00) % 28 === 0 ? '와' : '과';
}

/** `HH:MM`. 24:00 은 하루의 끝을 뜻하는 종료 시각 전용 값이다(core 의 스키마와 같은 규칙). */
const HHMM = /^([01]\d|2[0-4]):([0-5]\d)$/;


// ─────────────────────────── 캠페인 · 긴급 ───────────────────────────

/** 새 캠페인의 우선순위 — 편집 창의 [고급 설정] 은 이 값이 아닐 때 펼친 채로 열린다. */
export const CAMPAIGN_DEFAULT_PRIORITY = 100;

export const CAMPAIGN_SORT_KEYS = ['name', 'period', 'priority'] as const;
export type CampaignSortKey = (typeof CAMPAIGN_SORT_KEYS)[number];
const CAMPAIGN_STATUSES: readonly Campaign['status'][] = ['active', 'scheduled', 'ended', 'disabled'];

export function campaignSortValue(campaign: Campaign, key: CampaignSortKey): SortValue {
  if (key === 'name') return campaign.name || null;
  if (key === 'priority') return campaign.priority;
  // 시작일이 없으면 "처음부터" 다 — 가장 이른 것으로 둔다.
  return campaign.start_at ? Date.parse(campaign.start_at) : 0;
}

export function CampaignsScreen({ client, me }: Props): ReactNode {
  const campaigns = useAsync(() => client.listCampaigns(), [client]);
  const emergencies = useAsync(() => client.listEmergencies(), [client]);
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  // 대상 고르기의 그룹 — 담당 범위가 있으면 서버가 맡은 그룹만 준다.
  const groupList = useAsync(() => client.listGroups(), [client]);
  const groups = groupList.data?.items ?? [];
  const scope = me.scope ?? null;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  /** 복제 — 이 캠페인의 값을 채운 새 캠페인 창. */
  const [copying, setCopying] = useState<Campaign | null>(null);
  /** 긴급 송출 창. */
  const [raising, setRaising] = useState(false);
  const action = useAction();

  const items = campaigns.data?.items ?? [];
  const activeEmergency = emergencies.data?.items.find((e) => e.active);

  /*
   * 정렬·거르기. 기본은 서버 순서(우선순위 높은 것부터, 같으면 먼저 만든 것)이고, 이는 엔진이 고르는
   * 순서와 같다 — 다른 열로 정렬해도 같은 값끼리는 그 순서를 지킨다.
   */
  const [sort, toggleSort] = useTableSort<CampaignSortKey>('campaigns', CAMPAIGN_SORT_KEYS, {
    key: 'priority',
    dir: 'desc',
  });
  /* 상태 필터는 주소에 산다(`?status=`) — 새로 고쳐도, 링크로 보내도 그대로. */
  const url = useUrlQuery();
  const statusChosen = url.list('status');
  const filters: Filters<'status'> = { status: statusChosen };
  const rows = useMemo(
    () => sortRows(applyFilters(items, filters, (campaign) => campaign.status), sort, campaignSortValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터는 주소에서 매번 새로 읽어 내용으로 본다.
    [items, statusChosen.join(','), sort],
  );
  const filtering = (filters.status?.length ?? 0) > 0;
  /* 쪽 나누기 — 상태 필터·정렬을 바꾸면 1쪽으로(`Pager`). */
  const paging = usePaging(url, 'campaigns', JSON.stringify([statusChosen, sort.key, sort.dir]));
  const pageSlice = paginate(rows, paging.page, paging.size);
  /*
   * 일괄 작업 — 고칠 수 있는 줄(범위 안)만 고를 수 있고, 고른 것 가운데 조건에 맞는 줄만 센다. 다른 쪽에서 고른 것도
   * 센다(막대가 그 수를 적는다).
   */
  const canWrite = can(me, 'schedule:write');
  const selectable = rows.filter((campaign) => canWrite && withinScope(campaign.target, scope));
  const pageSelectable = pageSlice.rows.filter((campaign) => canWrite && withinScope(campaign.target, scope));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const chosen = selectable.filter((campaign) => selected.has(campaign.id));
  const setEnabledForChosen = (enabled: boolean): void => {
    const targets = chosen.filter((campaign) => campaign.enabled !== enabled);
    void action.run(
      () => runEach(targets, (campaign) => client.updateCampaign(campaign.id, { enabled })),
      () => {
        setSelected(new Set());
        campaigns.reload();
      },
      `캠페인 ${targets.length}개를 ${enabled ? '재개' : '중지'}했습니다`,
    );
  };
  const deleteChosen = async (): Promise<void> => {
    const targets = chosen;
    const ok = await confirmDialog({
      title: `캠페인 ${targets.length}개를 삭제할까요?`,
      body: `${targets
        .slice(0, 5)
        .map((campaign) => campaign.name)
        .join(', ')}${targets.length > 5 ? ` 외 ${targets.length - 5}개` : ''}\n\n되돌릴 수 없습니다. 잠시 내리려면 [중지] 를 쓰세요.`,
      confirmLabel: '삭제',
      danger: true,
    });
    if (!ok) return;
    void action.run(
      () => runEach(targets, (campaign) => client.deleteCampaign(campaign.id)),
      () => {
        setSelected(new Set());
        campaigns.reload();
      },
      `캠페인 ${targets.length}개를 삭제했습니다`,
    );
  };
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

      {/*
        발령 사실은 **누구나** 봐야 한다 — 지금 모든 화면이 이 편성을 틀고 있다는 사실은
        권한과 무관하게 알아야 할 정보다. 다만 해제는 `emergency:write` 를 가진 사람만
        할 수 있으므로 단추는 그 사람에게만 낸다.
      */}
      {activeEmergency ? (
        <div className="note note-emergency" role="alert">
          <span>
            <strong>긴급 오버라이드 발령 중</strong> — {activeEmergency.name}.{' '}
            {activeEmergency.blank
              ? `${activeEmergency.target ? targetLabel(activeEmergency.target) : '대상 화면'}을 비웠습니다(검은 화면).`
              : `${activeEmergency.target ? targetLabel(activeEmergency.target) : '모든 화면'}에서 재생 중입니다.`}
          </span>
          {can(me, 'emergency:write') ? (
            <button
              type="button"
              className="btn btn-danger"
              disabled={action.pending}
              onClick={() =>
                void action.run(() => client.clearEmergency(activeEmergency.id), () => {
                  emergencies.reload();
                })
              }
            >
              즉시 해제
            </button>
          ) : null}
        </div>
      ) : null}

      {/*
        긴급 발령 — 발령할 수 있는 사람에게만. 위기 때 공급사에 전화하지 않고 고객사가 직접 덮는다
        (2026-10-05 부터 관리자도 `emergency:write` 를 갖는다). 발령 중에는 새로 내지 않는다 —
        해석은 가장 최근 것 하나만 트므로, 둘을 겹치면 무엇이 나가는지 헷갈린다.
      */}
      {can(me, 'emergency:write') && !activeEmergency ? (
        <Panel
          title="긴급"
          help={`모든 편성(캠페인·스케줄·운영 시간)보다 먼저 나갑니다. 대상 화면에 약 30초 안에 반영되고, 꺼져 있던 화면은 켜지는 대로 받습니다. 발령·해제는 변경 기록에 남습니다. ${scope ? '전 화면 긴급은 전체 관리자가 냅니다.' : '그 콘텐츠만 내리려면 콘텐츠를 보관하면 됩니다.'}`}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setRaising(true)}>
                긴급 송출
              </button>
              {/*
                화면 비우기는 [⋯] 한 단계 뒤에 둔다 — 늘 보이는 빨간 단추는 평소에 잘못 눌리고, 위기 때는 한 번
                더 누르는 것이 늦지 않다(어차피 확인 창을 거친다). 긴급 송출 창 안에서도 고를 수 있다.
              */}
              <MoreMenu
                size="normal"
                label="긴급 작업 더 보기"
                items={[
                  {
                    label: scope ? '맡은 화면 비우기' : '전 화면 비우기',
                    danger: true,
                    disabled: action.pending,
                    onSelect: async () => {
                      // 담당 범위가 있으면 맡은 그룹의 화면만 비운다 — 전 화면은 전체 관리자의 몫이다.
                      const ok = await confirmDialog({
                        title: scope ? '맡은 화면을 지금 비울까요?' : '모든 화면을 지금 비울까요?',
                        body: scope
                          ? `맡은 그룹(${scope.groups.map((group) => group.name).join(', ')})의 화면이 검은 화면이 됩니다. 해제할 때까지 아무것도 나가지 않습니다.`
                          : '모든 화면이 검은 화면이 됩니다. 해제할 때까지 아무것도 나가지 않습니다.',
                        confirmLabel: scope ? '맡은 화면 비우기' : '전 화면 비우기',
                        danger: true,
                      });
                      if (!ok) return;
                      void action.run(
                        () =>
                          client.createEmergency(
                            scope
                              ? { name: '맡은 화면 비우기', blank: true, target: { kind: 'groups', group_ids: scope.group_ids } }
                              : { name: '전 화면 비우기', blank: true, target: { kind: 'all' } },
                          ),
                        emergencies.reload,
                      );
                    },
                  },
                ]}
              />
            </>
          }
        >
          <p className="hint p-4">
            {scope
              ? '재난·사고 안내처럼 맡은 화면을 즉시 덮어야 할 때 씁니다. 잘못 나간 콘텐츠를 당장 걷으려면 [⋯] ▸ [맡은 화면 비우기].'
              : '재난·사고 안내처럼 모든 화면을 즉시 덮어야 할 때 씁니다. 잘못 나간 콘텐츠를 당장 걷으려면 [⋯] ▸ [전 화면 비우기].'}
          </p>
        </Panel>
      ) : null}

      <Panel
        title={`캠페인 ${items.length}개`}
        className="screen-fill"
        help="기간·대상을 정해 평소 편성 위에 게재합니다. 기간이 끝나면 자동으로 평소 편성으로 돌아갑니다."
        actions={
          can(me, 'schedule:write') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              새 캠페인
            </button>
          ) : null
        }
      >
        {campaigns.loading && items.length === 0 ? <Loading /> : null}

        {!campaigns.loading && items.length === 0 ? (
          <Empty
            title="캠페인이 없습니다"
            hint="광고·연휴 편성처럼 기간을 정해 게재하려면 새 캠페인을 만드세요."
          />
        ) : null}

        {filtering && items.length > 0 ? (
          <FilterNote shown={rows.length} total={items.length} unit="개" onClear={() => url.set({ status: null })} />
        ) : null}

        {chosen.length > 0 ? (
          <SelectionBar
            count={chosen.length}
            unit="개"
            onCancel={() => setSelected(new Set())}
            note={
              <PageSelectionNote
                pageIds={pageSelectable.map((campaign) => campaign.id)}
                allIds={selectable.map((campaign) => campaign.id)}
                selected={selected}
                unit="개"
                onChange={setSelected}
              />
            }
          >
            <button
              type="button"
              className="btn"
              disabled={action.pending || chosen.every((campaign) => !campaign.enabled)}
              onClick={() => setEnabledForChosen(false)}
            >
              중지
            </button>
            <button
              type="button"
              className="btn"
              disabled={action.pending || chosen.every((campaign) => campaign.enabled)}
              onClick={() => setEnabledForChosen(true)}
            >
              재개
            </button>
            <button type="button" className="btn btn-danger" disabled={action.pending} onClick={() => void deleteChosen()}>
              삭제
            </button>
          </SelectionBar>
        ) : null}

        {items.length > 0 ? (
          <table className="table min-w-[44rem] lg:min-w-0">
            <thead>
              <tr>
                {canWrite ? (
                  <th className="col-check">
                    <SelectAll ids={pageSelectable.map((campaign) => campaign.id)} selected={selected} onChange={setSelected} />
                  </th>
                ) : null}
                <ColumnHead label="이름" column="name" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                <ColumnHead
                  label="상태"
                  column="status"
                  filter={
                    <FilterMenu
                      label="상태"
                      options={filterOptions(
                        items,
                        (campaign) => campaign.status,
                        (value) => ({ label: statusLabel(value as Campaign['status']) }),
                        filters.status ?? [],
                        fixedOrder(CAMPAIGN_STATUSES),
                      )}
                      chosen={filters.status ?? []}
                      onChange={(chosen) => url.set({ status: chosen })}
                    />
                  }
                />
                <th>방식</th>
                <th>대상</th>
                <ColumnHead label="기간" column="period" sorting={sorting({ asc: '시작 빠른 순', desc: '시작 늦은 순' })} />
                {/* 높은 것이 먼저다 — 엔진이 고르는 순서. */}
                <ColumnHead
                  label="우선순위"
                  column="priority"
                  sorting={sorting({ asc: '낮은 순', desc: '높은 순' }, 'desc')}
                />
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="row-empty">
                  <td colSpan={canWrite ? 8 : 7}>조건에 맞는 캠페인이 없습니다.</td>
                </tr>
              ) : null}
              {pageSlice.rows.map((campaign) => (
                <tr key={campaign.id} className={campaign.enabled ? '' : 'row-muted'}>
                  {canWrite ? (
                    <td className="col-check">
                      {/* 범위 밖 캠페인은 고를 수 없다 — 고쳐도 서버가 막는다. */}
                      {withinScope(campaign.target, scope) ? (
                        <input
                          type="checkbox"
                          aria-label={`${campaign.name} 선택`}
                          checked={selected.has(campaign.id)}
                          onChange={() =>
                            setSelected((current) => {
                              const next = new Set(current);
                              if (next.has(campaign.id)) next.delete(campaign.id);
                              else next.add(campaign.id);
                              return next;
                            })
                          }
                        />
                      ) : null}
                    </td>
                  ) : null}
                  {/* 이름을 누르면 [편집] 과 같다 — 고칠 수 없거나 담당 범위 밖이면 글자로만. */}
                  <td>
                    <ItemTitle
                      onOpen={
                        can(me, 'schedule:write') && withinScope(campaign.target, scope)
                          ? () => setEditing(campaign.id)
                          : undefined
                      }
                      label={`${campaign.name} 편집`}
                    >
                      {campaign.name}
                    </ItemTitle>
                  </td>
                  {/*
                    **"게재 중" 만으로는 나가는지 알 수 없다.** 게재 중인 캠페인이 둘이면
                    엔진은 하나만 고르고(우선순위 → 먼저 만든 쪽) 나머지는 어느 화면에도
                    나가지 않는다. 그 사실을 여기서 말하지 않으면, 광고가 안 나간 이유를
                    찾는 사람이 이 줄을 보고 "나가고 있다" 고 판단한다.
                  */}
                  <td>
                    <Badge tone={statusTone(campaign.status)}>{statusLabel(campaign.status)}</Badge>
                    {(() => {
                      const winner = hiddenBy(campaign, items);
                      return winner ? (
                        <span
                          className="ml-1 whitespace-nowrap text-[11.5px] text-amber-700"
                          title={`"${winner.name}"(우선순위 ${winner.priority})에 가려 재생되지 않습니다. 우선순위가 같으면 먼저 만든 캠페인이 우선합니다.`}
                        >
                          가려짐 · {winner.name}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td>
                    {campaign.mode === 'blend' ? (
                      <Badge tone="info">{campaign.blend_every}개마다 삽입</Badge>
                    ) : (
                      <Badge tone="neutral">전체 대체</Badge>
                    )}
                  </td>
                  <td className="cell-muted">{targetLabel(campaign.target)}</td>
                  <td className="cell-muted">{periodLabel(campaign)}</td>
                  <td className="cell-muted">{campaign.priority}</td>
                  <td className="col-actions">
                    {can(me, 'schedule:write') && !withinScope(campaign.target, scope) ? (
                      <span className="hint" title="담당 범위 밖의 화면에도 걸린 캠페인입니다. 전체 관리자가 고칩니다.">
                        범위 밖
                      </span>
                    ) : can(me, 'schedule:write') ? (
                    <>
                    <IconButton icon={Pencil} label="편집" subject={campaign.name} onClick={() => setEditing(campaign.id)} />
                    {/*
                      중지·재개는 자주 쓰는 일이라 밖에, 복제·삭제는 [⋯] 뒤에(점진적 공개).
                      복제는 원본 값을 채운 새 캠페인 창 — 저장해야 생긴다(기간·대상만 바꿔 다시 거는 일이 많다).
                    */}
                    <IconButton
                      icon={campaign.enabled ? Pause : Play}
                      label={campaign.enabled ? '중지' : '재개'}
                      subject={campaign.name}
                      disabled={action.pending}
                      onClick={() =>
                        void action.run(
                          () => client.updateCampaign(campaign.id, { enabled: !campaign.enabled }),
                          campaigns.reload,
                          campaign.enabled ? `"${campaign.name}" 캠페인을 중지했습니다` : `"${campaign.name}" 캠페인을 재개했습니다`,
                        )
                      }
                    />
                    <MoreMenu
                      label={`${campaign.name} 작업`}
                      items={[
                        { label: '복제', onSelect: () => setCopying(campaign) },
                        {
                          label: '삭제',
                          danger: true,
                          onSelect: async () => {
                            const ok = await confirmDialog({
                              title: '캠페인을 삭제할까요?',
                              body: <><strong>{campaign.name}</strong> — 되돌릴 수 없습니다.</>,
                              confirmLabel: '삭제',
                              danger: true,
                            });
                            if (!ok) return;
                            void action.run(
                              () => client.deleteCampaign(campaign.id),
                              campaigns.reload,
                              `"${campaign.name}" 캠페인을 삭제했습니다`,
                            );
                          },
                        },
                      ]}
                    />
                    </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <Pager slice={pageSlice} paging={paging} unit="개" />
      </Panel>

      {raising && playlists.data ? (
        <EmergencyForm
          client={client}
          playlists={playlists.data.items}
          groups={groups}
          scoped={scope !== null}
          onClose={() => setRaising(false)}
          onRaised={() => {
            setRaising(false);
            emergencies.reload();
          }}
        />
      ) : null}

      {(creating || editing || copying) && playlists.data ? (
        <CampaignEditor
          client={client}
          playlists={playlists.data.items}
          groups={groups}
          scoped={scope !== null}
          existing={items.find((c) => c.id === editing) ?? null}
          copyOf={copying ? { ...copying, name: copyName(copying.name, items.map((other) => other.name)) } : null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setCopying(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            setCopying(null);
            campaigns.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function statusTone(status: Campaign['status']): 'ok' | 'info' | 'neutral' {
  if (status === 'active') return 'ok';
  if (status === 'scheduled') return 'info';
  return 'neutral';
}

function statusLabel(status: Campaign['status']): string {
  return { active: '게재 중', scheduled: '예정', ended: '종료', disabled: '중지됨' }[status];
}

/**
 * 저 캠페인이 이 캠페인을 **반드시** 이기는가 — 해석 엔진과 같은 규칙.
 *
 * 순서는 `packages/core/src/campaign.ts` 의 `compareCampaigns` 와 같다:
 * 우선순위 내림차순 → 먼저 만든 쪽 → id. 엔진은 게재 중인 캠페인 중 **하나만** 고르므로,
 * 진 캠페인은 전체 대체든 삽입이든 아무것도 내보내지 못한다.
 */
function beats(winner: Campaign, loser: Campaign): boolean {
  if (winner.priority !== loser.priority) return winner.priority > loser.priority;
  const a = Date.parse(winner.created_at);
  const b = Date.parse(loser.created_at);
  if (a !== b) return a < b;
  return winner.id < loser.id;
}

/**
 * 저 대상이 이 대상을 **남김없이 덮는가.**
 *
 * 덮을 때만 "언제나 가려진다" 고 말할 수 있다. 태그가 서로 엇갈리면 화면마다 답이 달라
 * (어떤 화면은 이쪽, 어떤 화면은 저쪽) 목록 한 줄로 단정할 수 없다 — 그럴 때는 아무 말도
 * 하지 않는다. **틀린 표시는 없느니만 못하다.** 화면별 정답은 화면 상세의 판단 근거에 있다.
 */
function covers(wide: Campaign['target'], narrow: Campaign['target']): boolean {
  if (wide.kind === 'all') return true;
  if (wide.kind !== narrow.kind) return false;
  if (wide.kind === 'tags') return narrow.tags.every((tag) => wide.tags.includes(tag));
  if (wide.kind === 'groups') return narrow.group_ids.every((id) => wide.group_ids.includes(id));
  return narrow.display_ids.every((id) => wide.display_ids.includes(id));
}

/**
 * 이 캠페인을 가리고 있는 캠페인 — 없으면 null.
 *
 * 게재 중인 캠페인이 둘이면 목록에는 둘 다 "게재 중" 으로 서는데, 실제로 나가는 것은 하나뿐이다.
 * 나머지는 켜져 있고 기간 안이면서도 **어느 화면에도 나가지 않는다** — 광고가 안 나간 이유를
 * 찾는 사람이 가장 늦게 의심하는 자리다.
 */
export function hiddenBy(campaign: Campaign, all: readonly Campaign[]): Campaign | null {
  if (campaign.status !== 'active') return null;
  const winners = all.filter(
    (other) =>
      other.id !== campaign.id &&
      other.status === 'active' &&
      beats(other, campaign) &&
      covers(other.target, campaign.target),
  );
  if (winners.length === 0) return null;
  // 여럿이면 그중에서도 이기는 하나를 지목한다 — 사람이 찾아갈 곳은 한 곳이어야 한다.
  return [...winners].sort((a, b) => (beats(a, b) ? -1 : 1))[0] ?? null;
}

export function targetLabel(target: Campaign['target']): string {
  if (target.kind === 'all') return '전체 화면';
  if (target.kind === 'tags') return `태그: ${target.tags.join(', ')}`;
  if (target.kind === 'groups') return `그룹 ${target.group_ids.length}개`;
  return `화면 ${target.display_ids.length}대`;
}

/*
 * 캠페인 기간은 **이 브라우저의 하루**로 읽고 쓴다(`components/localDate.ts`). 예전에는 입력칸의
 * "2026-06-30" 을 그대로 보냈고 서버가 UTC 자정으로 읽어, 한국에서는 **종료일 오전 9시에
 * 끝났다** — 종료일로 적은 날은 거의 게재되지 않았다. 목록의 날짜도 ISO 를 잘라 쓴 UTC 날짜였다.
 */

export type TargetKind = 'all' | 'tags' | 'groups' | 'keep';

/**
 * 저장할 대상.
 *
 * 편집기가 고를 수 있는 것은 "전체" · "태그" · "그룹"(2026-10-05 — 사옥·지점 그룹)이다. 화면 하나하나를
 * 고른 대상은 연동 시스템(API)이 정한 것이라, 예전에는 그런 캠페인을 열면 "전체 화면" 이 골라진 채 떠서
 * **이름만 고쳐 저장해도 대상이 전체로 바뀌었다.** 그대로 두기를 기본으로 두고, 바꾸는 것은 사람이 고를 때만.
 */
export function campaignTargetPayload(
  kind: TargetKind,
  tags: string,
  existing: Campaign['target'] | null,
  groupIds: readonly string[] = [],
): Campaign['target'] | { kind: 'all' } | { kind: 'tags'; tags: string[] } | { kind: 'groups'; group_ids: string[] } {
  if (kind === 'keep' && existing) return existing;
  if (kind === 'tags') return { kind: 'tags', tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean) };
  if (kind === 'groups') return { kind: 'groups', group_ids: [...groupIds] };
  return { kind: 'all' };
}

/**
 * 대상 고르기 — 캠페인 편집기와 긴급 송출이 함께 쓴다.
 *
 * **담당 범위가 있는 사람은 그룹만 고른다.** 전체·태그는 맡은 범위 밖의 화면까지 닿을 수 있어 서버가 거절한다
 * (`services/scope.ts`). 그룹 목록은 서버가 이미 맡은 것만 준다.
 */
function TargetPicker({
  kind,
  onKind,
  tags,
  onTags,
  groupIds,
  onGroupIds,
  groups,
  scoped,
  keepLabel,
  tagPlaceholder,
}: {
  kind: TargetKind;
  onKind: (kind: TargetKind) => void;
  tags: string;
  onTags: (tags: string) => void;
  groupIds: readonly string[];
  onGroupIds: (ids: string[]) => void;
  groups: readonly Group[];
  scoped: boolean;
  /** 화면 하나하나를 고른 기존 대상 — "그대로" 를 고를 수 있게. */
  keepLabel: string | null;
  tagPlaceholder: string;
}): ReactNode {
  return (
    <>
      <div className="radios">
        {keepLabel ? (
          <label>
            <input type="radio" checked={kind === 'keep'} onChange={() => onKind('keep')} />
            현재 대상 유지 ({keepLabel})
          </label>
        ) : null}
        {scoped ? null : (
          <>
            <label>
              <input type="radio" checked={kind === 'all'} onChange={() => onKind('all')} />
              전체 화면
            </label>
            <label>
              <input type="radio" checked={kind === 'tags'} onChange={() => onKind('tags')} />
              태그로 지정
            </label>
          </>
        )}
        <label>
          <input type="radio" checked={kind === 'groups'} onChange={() => onKind('groups')} />
          {scoped ? '맡은 그룹에서 고르기' : '그룹으로 지정'}
        </label>
      </div>
      {kind === 'tags' ? (
        <input className="input" value={tags} onChange={(e) => onTags(e.target.value)} placeholder={tagPlaceholder} />
      ) : null}
      {kind === 'groups' ? (
        groups.length === 0 ? (
          <small className="hint">화면 그룹이 없습니다 — [화면 그룹] 메뉴에서 그룹을 먼저 만드세요.</small>
        ) : (
          <div className="scope-picker">
            {groups.map((group) => (
              <label key={group.id} className="scope-picker-item">
                <input
                  type="checkbox"
                  checked={groupIds.includes(group.id)}
                  onChange={(event) =>
                    onGroupIds(event.target.checked ? [...groupIds, group.id] : groupIds.filter((id) => id !== group.id))
                  }
                />
                <span>{group.name}</span>
                <span className="cell-muted">화면 {group.display_count}대</span>
              </label>
            ))}
          </div>
        )
      ) : null}
    </>
  );
}

/**
 * 이 대상이 담당 범위 안인가 — 범위 밖 캠페인에는 편집 단추를 두지 않는다(서버가 403 으로 거절한다).
 *
 * 화면 하나하나를 고른 대상은 화면이 어느 그룹인지 콘솔이 모르므로 단추를 남겨 두고 서버에 맡긴다.
 */
export function withinScope(target: Campaign['target'], scope: { group_ids: readonly string[] } | null): boolean {
  if (!scope) return true;
  if (target.kind === 'groups') return target.group_ids.every((id) => scope.group_ids.includes(id));
  return target.kind === 'displays';
}

/** 고른 대상이 아직 비어 있는가 — 저장 단추를 막는다. */
export function targetIncomplete(kind: TargetKind, tags: string, groupIds: readonly string[]): boolean {
  if (kind === 'tags') return !tags.trim();
  if (kind === 'groups') return groupIds.length === 0;
  return false;
}

export function periodLabel(campaign: Campaign): string {
  const start = campaign.start_at ? localDate(campaign.start_at) : '무기한';
  const end = campaign.end_at ? localDate(campaign.end_at) : '무기한';
  const period = `${start} ~ ${end}`;
  if (campaign.dayparts.length === 0) return period;
  // 구간이 여럿이면 전부 보여준다. 첫 구간만 보여주면 나머지가 있다는 것조차 안 보인다.
  const parts = campaign.dayparts
    .map((part) => `${formatDays(part.days)} ${formatDaypart(part.start, part.end)}`)
    .join(', ');
  return `${period} · ${parts}`;
}

/**
 * 캠페인 편집기 — 만들 때와 고칠 때 **같은 화면**을 쓴다.
 *
 * 예전에는 만들기만 있었다. 이름 하나를 고치려 해도 지우고 다시 만들어야 했고, 다시 만들면
 * 기간·대상·우선순위를 전부 손으로 옮겨 적게 된다 — 그 과정에서 하나쯤 빠뜨린다.
 * 서버는 처음부터 PATCH 를 받고 있었다.
 */
function CampaignEditor({
  client,
  playlists,
  groups,
  scoped,
  existing,
  copyOf = null,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  playlists: Playlist[];
  groups: Group[];
  /** 담당 범위가 있는 사람 — 대상을 맡은 그룹에서만 고른다. */
  scoped: boolean;
  existing: Campaign | null;
  /** 복제 — 이 값을 채운 채 **새로 만든다**(저장해야 생긴다). 이름은 부르는 쪽이 사본 이름으로 준다. */
  copyOf?: Campaign | null;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  /** 처음 값을 채울 원본 — 고치는 것이면 그것, 복제면 복제할 것. */
  const source = existing ?? copyOf;
  const [name, setName] = useState(source?.name ?? '');
  const [playlistId, setPlaylistId] = useState(source?.playlist_id ?? playlists[0]?.id ?? '');
  const [mode, setMode] = useState<'replace' | 'blend'>(source?.mode ?? 'replace');
  const [blendEvery, setBlendEvery] = useState<number | null>(source?.blend_every ?? 3);
  const [priority, setPriority] = useState<number | null>(source?.priority ?? CAMPAIGN_DEFAULT_PRIORITY);
  /** [저장] 을 눌렀는데 숫자 칸(삽입 간격·우선순위)을 고쳐야 했다 — 그 칸을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [startAt, setStartAt] = useState(source?.start_at ? localDate(source.start_at) : '');
  const [endAt, setEndAt] = useState(source?.end_at ? localDate(source.end_at) : '');
  // 화면 하나하나를 고른 대상은 이 편집기가 만들 수 없다 — 열었을 때는 "그대로" 가 골라져 있어야 한다.
  const keepable = source?.target.kind === 'displays';
  const [targetKind, setTargetKind] = useState<TargetKind>(
    keepable
      ? 'keep'
      : source?.target.kind === 'tags'
        ? 'tags'
        : source?.target.kind === 'groups' || scoped
          ? 'groups'
          : 'all',
  );
  const [tags, setTags] = useState(source?.target.tags.join(', ') ?? '');
  const [groupIds, setGroupIds] = useState<string[]>(source?.target.group_ids ?? []);
  const [dayparts, setDayparts] = useState<Daypart[]>(source?.dayparts ?? []);
  /* 고친 것이 있으면 닫기 전에 묻는다. */
  const close = useGuardedClose(
    useDirty({ name, playlistId, mode, blendEvery, priority, startAt, endAt, targetKind, tags, groupIds, dayparts }),
    onClose,
  );
  const action = useAction();

  const addDaypart = (): void => setDayparts([...dayparts, { days: [1, 2, 3, 4, 5], start: '11:30', end: '13:00' }]);
  const updateDaypart = (index: number, patch: Partial<Daypart>): void =>
    setDayparts(dayparts.map((part, i) => (i === index ? { ...part, ...patch } : part)));

  /* 숫자 칸의 문제 — 삽입 간격은 섞어 넣기일 때만 보인다. */
  const blendError = mode === 'blend' ? numberProblem(blendEvery, BLEND_RULE) : null;
  const priorityError = numberProblem(priority, PRIORITY_RULE);

  const save = (): void => {
    if (blendError || priorityError) {
      setShowErrors(true);
      focusFirstInvalid(formRef.current);
      return;
    }
    setShowErrors(false);
    const payload = {
      name,
      playlist_id: playlistId,
      mode,
      // 섞어 넣기가 아니면 칸이 없다 — 비워 둔 채 방식을 바꿨으면 원래 값을 보낸다.
      blend_every: blendEvery ?? source?.blend_every ?? 3,
      priority: priority ?? CAMPAIGN_DEFAULT_PRIORITY,
      start_at: startAt ? dayStartIso(startAt) : null,
      end_at: endAt ? dayEndIso(endAt) : null,
      target: campaignTargetPayload(targetKind, tags, source?.target ?? null, groupIds),
      dayparts,
    };
    void action.run(
      () => (existing ? client.updateCampaign(existing.id, payload) : client.createCampaign(payload)),
      onSaved,
      existing ? `"${name}" 캠페인을 저장했습니다` : `"${name}" 캠페인을 만들었습니다`,
    );
  };

  return (
    <Modal title={existing ? '캠페인 편집' : copyOf ? '캠페인 복제' : '새 캠페인'} onClose={close}>
      <div className="form" ref={formRef}>
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <label className="field">
          <span>이름</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <div className="field">
          <span>플레이리스트</span>
          <PlaybackPicker
            label="캠페인 플레이리스트"
            placeholder="플레이리스트 고르기"
            value={playlistId ? { kind: 'playlist', id: playlistId } : null}
            sources={{ playlists }}
            onChange={(next) => setPlaylistId(next?.id ?? '')}
          />
        </div>

        <div className="field">
          <span>
            재생 방식
            <HelpTip
              text="전체 대체는 기존 편성 대신 캠페인만 재생하고, 삽입은 기존 편성 사이에 끼워 재생합니다."
              label="재생 방식 도움말"
            />
          </span>
          <div className="radios">
            <label>
              <input
                type="radio"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              전체 대체
            </label>
            <label>
              <input type="radio" checked={mode === 'blend'} onChange={() => setMode('blend')} />
              삽입
            </label>
          </div>
          {mode === 'blend' ? (
            <label className="field">
              <span>
                삽입 간격
                <HelpTip
                  text="삽입 위치는 화면마다 분산되어, 모든 화면이 동시에 같은 광고를 재생하지 않습니다."
                  label="삽입 간격 도움말"
                />
              </span>
              <NumberInput
                rule={BLEND_RULE}
                value={blendEvery}
                invalid={showErrors && blendError !== null}
                onChange={setBlendEvery}
              />
              {showErrors && blendError ? (
                <p className="field-error" role="alert">
                  {blendError}
                </p>
              ) : (
                <small className="hint">
                  {blendEvery === null ? '기존 콘텐츠 몇 개마다 1개씩 삽입할지 정합니다.' : `기존 콘텐츠 ${blendEvery}개마다 1개씩 삽입합니다.`}
                </small>
              )}
            </label>
          ) : null}
        </div>

        <div className="field">
          <span>
            대상
            <HelpTip
              text={
                scoped
                  ? '담당 범위가 정해진 계정은 맡은 그룹에만 캠페인을 걸 수 있습니다.'
                  : keepable
                    ? '화면 대상은 연동 시스템에서 지정한 것입니다. 여기서는 유지하거나 전체·태그·그룹으로 바꿀 수 있습니다.'
                    : '태그는 입력한 것 중 하나라도 붙은 화면에, 그룹은 고른 그룹의 화면에 게재합니다. 태그는 쉼표로 구분합니다.'
              }
              label="대상 도움말"
            />
          </span>
          <TargetPicker
            kind={targetKind}
            onKind={setTargetKind}
            tags={tags}
            onTags={setTags}
            groupIds={groupIds}
            onGroupIds={setGroupIds}
            groups={groups}
            scoped={scoped}
            keepLabel={keepable && existing ? targetLabel(existing.target) : null}
            tagPlaceholder="로비, 수도권"
          />
        </div>

        <div className="grid-2">
          <label className="field">
            <span>
              시작일
              <HelpTip
                text="시작일 0시부터 종료일 24시까지 게재합니다. 비워 두면 그쪽은 기한이 없습니다."
                label="게재 기간 도움말"
              />
            </span>
            <DateField value={startAt} onChange={setStartAt} label="시작일" clearable />
          </label>
          <label className="field">
            <span>종료일</span>
            <DateField value={endAt} onChange={setEndAt} label="종료일" clearable />
          </label>
        </div>

        {/*
          구간을 여러 개 둘 수 있다. "점심 그리고 저녁" 은 흔한 편성인데 한 구간만 받던
          시절에는 캠페인을 둘로 쪼개야 했고, 그러면 기간·대상·우선순위를 두 벌 관리하게 된다.
          서버는 처음부터 20개까지 받고 있었다.
        */}
        <div className="field">
          <span>게재 시간대</span>
          {dayparts.length === 0 ? (
            <p className="hint">지정하지 않으면 기간 내 하루 종일 게재합니다.</p>
          ) : null}

          {dayparts.map((part, index) => (
            <div key={index} className="rule-editor">
              <DayPicker days={part.days} onChange={(days) => updateDaypart(index, { days })} />
              {/* 운영 시간과 같은 시각 칸 — 적거나 시·분 목록에서 고른다(`TimeField`). */}
              <TimeField value={part.start} onChange={(start) => updateDaypart(index, { start })} label="시작 시각" />
              <TimeField value={part.end} onChange={(end) => updateDaypart(index, { end })} label="종료 시각" />
              {part.end <= part.start ? <Badge tone="warn">익일까지</Badge> : null}
              <button
                type="button"
                className="link link-danger"
                onClick={() => setDayparts(dayparts.filter((_, i) => i !== index))}
              >
                제거
              </button>
            </div>
          ))}

          <button type="button" className="btn" onClick={addDaypart} disabled={dayparts.length >= 20}>
            시간대 추가
          </button>
        </div>

        {/*
          우선순위는 캠페인이 겹칠 때만 뜻이 있다 — 처음 거는 사람에게 숫자 칸부터 보이면 무엇을 적어야 하나에서
          멈춘다. 펼친 채로 열고, 접으면 머리에 지금 값을 적는다(바꿔 둔 값을 다음 사람이 못 보면 안 된다).
        */}
        <Disclosure
          title="고급 설정"
          summary={`우선순위 ${priority ?? '—'}${priority === CAMPAIGN_DEFAULT_PRIORITY ? ' · 기본' : ''}`}
          forceOpen={showErrors && priorityError !== null}
        >
          <label className="field">
            <span>
              우선순위
              <HelpTip
                text="겹치는 캠페인 중 우선순위가 높은 쪽이 게재됩니다. 같으면 먼저 만든 캠페인이 우선합니다."
                label="캠페인 우선순위 도움말"
              />
            </span>
            <NumberInput
              rule={PRIORITY_RULE}
              value={priority}
              invalid={showErrors && priorityError !== null}
              onChange={setPriority}
            />
            {showErrors && priorityError ? (
              <p className="field-error" role="alert">
                {priorityError}
              </p>
            ) : null}
          </label>
        </Disclosure>

        <div className="form-actions">
          {existing ? (
            <span className="mr-auto">
              <HistoryButton client={client} type="campaign" id={existing.id} canRestore onRestored={onSaved} />
            </span>
          ) : null}
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={action.pending || !name || !playlistId || targetIncomplete(targetKind, tags, groupIds)}
            onClick={save}
          >
            {action.pending ? '저장 중…' : existing ? '저장' : '만들기'}

          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * 긴급 송출 — 무엇을(플레이리스트 또는 화면 비우기), 어디에(전체·태그), 언제까지.
 *
 * **보내기 전에 한 번 더 확인한다.** 이 창의 [발령] 은 대상 화면 전부를 즉시 덮는다. 편성 화면의
 * 다른 저장과 같은 무게로 두면, 연습 삼아 눌러 본 것이 로비 전체에 나간다.
 */
function EmergencyForm({
  client,
  playlists,
  groups,
  scoped,
  onClose,
  onRaised,
}: {
  client: SignageOneClient;
  playlists: Playlist[];
  groups: Group[];
  scoped: boolean;
  onClose: () => void;
  onRaised: () => void;
}): ReactNode {
  const [name, setName] = useState('긴급 안내');
  const [what, setWhat] = useState<'playlist' | 'blank'>(playlists.length > 0 ? 'playlist' : 'blank');
  const [playlistId, setPlaylistId] = useState(playlists[0]?.id ?? '');
  const [targetKind, setTargetKind] = useState<TargetKind>(scoped ? 'groups' : 'all');
  const [tags, setTags] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>(scoped ? groups.map((group) => group.id) : []);
  const [until, setUntil] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const action = useAction();

  const target = campaignTargetPayload(targetKind, tags, null, groupIds);
  const targetText =
    targetKind === 'all'
      ? '모든 화면'
      : targetKind === 'groups'
        ? `고른 그룹(${groups.filter((group) => groupIds.includes(group.id)).map((group) => group.name).join(', ') || '—'})의 화면`
        : `태그(${tags || '—'})가 붙은 화면`;

  return (
    <Modal title="긴급 송출" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const endAt = until ? new Date(until).toISOString() : null;
          void action.run(
            () =>
              client.createEmergency(
                what === 'blank'
                  ? { name, blank: true, target, end_at: endAt }
                  : { name, playlist_id: playlistId, target, end_at: endAt },
              ),
            onRaised,
          );
        }}
      >
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="field">
          <span>이름</span>
          <input className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} required />
          <small className="hint">변경 기록과 발령 중 안내에 이 이름이 남습니다.</small>
        </label>

        <div className="field">
          <span>내보낼 것</span>
          <div className="radios">
            <label>
              <input type="radio" checked={what === 'playlist'} disabled={playlists.length === 0} onChange={() => setWhat('playlist')} />
              플레이리스트 송출
            </label>
            <label>
              <input type="radio" checked={what === 'blank'} onChange={() => setWhat('blank')} />
              화면 비우기(검은 화면)
            </label>
          </div>
          {what === 'playlist' ? (
            <PlaybackPicker
              label="긴급 송출 플레이리스트"
              placeholder="플레이리스트 고르기"
              value={playlistId ? { kind: 'playlist', id: playlistId } : null}
              sources={{ playlists }}
              onChange={(next) => setPlaylistId(next?.id ?? '')}
            />
          ) : (
            <small className="hint">대상 화면이 아무것도 틀지 않습니다. 잘못 나간 것을 당장 걷을 때 씁니다.</small>
          )}
        </div>

        <div className="field">
          <span>대상</span>
          <TargetPicker
            kind={targetKind}
            onKind={setTargetKind}
            tags={tags}
            onTags={setTags}
            groupIds={groupIds}
            onGroupIds={setGroupIds}
            groups={groups}
            scoped={scoped}
            keepLabel={null}
            tagPlaceholder="본사, 로비"
          />
        </div>

        <label className="field">
          <span>
            끝낼 시각 <span className="font-normal text-slate-400">(선택)</span>
          </span>
          <DateTimeField value={until} onChange={setUntil} label="끝낼 시각" defaultTime="23:59" />
          <small className="hint">비우면 [즉시 해제] 를 누를 때까지 계속됩니다.</small>
        </label>

        <label className="flex items-start gap-2 text-[13px]">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>
            {targetText}을 지금 덮습니다. 모든 편성보다 먼저 나가며, 해제할 때까지 이어집니다.
          </span>
        </label>

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button
            type="submit"
            className="btn btn-danger"
            disabled={
              action.pending ||
              !confirmed ||
              !name.trim() ||
              (what === 'playlist' && !playlistId) ||
              targetIncomplete(targetKind, tags, groupIds)
            }
          >
            {action.pending ? '발령 중…' : '발령'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
